import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ConfigProvider } from 'antd';
import enUS from 'antd/lib/locale/en_US';
import frCA from 'antd/lib/locale/fr_CA';
import classnames from 'classnames';
import { TFunction } from 'i18next';
import _ from 'lodash';
import { DateTime } from 'luxon';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { create } from 'zustand';
import { ANALYTICS_EVENTS, track } from '../analytics';
import { patientApi, reportingApi } from '../api';
import {
  MealResponse,
  PatientActivityFeedRowExerciseLog,
  PatientActivityFeedRowMealLog,
  PatientActivityFeedRowMedicationLog,
  PatientEmotionalHealthResponse,
  PatientEmotionResponse,
  PatientMindfulnessResponse,
  PatientNutritionDetailsTableRow,
  PatientSleepDataResponse,
  PatientStepsDataResponse,
  PatientStressResponse,
} from '../api/generated/models';
import { useCgmData, useProcessedCgmData } from '../components/cgm/CGMDataService';
import { CGMGlucoseChart, CGMReportMarkerPoint } from '../components/cgm/CGMGlucoseChart';
import { DateRangeDropdown, useDateRange } from '../components/dateRangeFilter';
import ExerciseChart from '../components/ExerciseChart';
import { RangePickerWithActivityIndicators } from '../components/foodFilterNew';
import { HeartRateChart } from '../components/HRChart';
import InsulinChart from '../components/InsulinChart';
import { LoadingSpinner } from '../components/loadingSpinner';
import {
  EmotionCard,
  ExerciseCard,
  getNutrientLabelDefs,
  MealCard,
  mealItemIsVerifying,
  MedicationCard,
} from '../components/mealCard';
import MealCompositionChart from '../components/MealCompositionChart';
import { MindfulEatingSurveyIcon } from '../components/patientSurvey';
import { AutoScrollHere, ShowMealV2, useRecentlyModifiedMeal } from '../components/showMealV2';
import { getSleepSegments, SleepStage, SleepStageType } from '../components/sleep/sleepChartModel';
import { SleepStagesChart } from '../components/sleep/SleepStagesChart';
import { useCalendarActivityIndicators } from '../components/useCalendarActivityFeed';
import { WaterLog } from '../components/waterLog';
import { useCurrentPatientData, useStore } from '../context';
import { TFunc, Trans, useTranslation } from '../i18n';
import {
  useHealthDataTable,
  useProcessedHeartRateData,
  useProcessedOxygenData,
  useSleepChartData,
} from '../services/HealthDataService';
import EmotionImage from '../theme/icons/emotion.svg';
import ExerciseImage from '../theme/icons/exercise.svg';
import InsulinImage from '../theme/icons/insulin.svg';
import MealImage from '../theme/icons/meal.svg';
import { formatDateLongMonth, MealName, parseLocalDateTimeLux } from '../utils/formatDate';
import { formatNumber, titleCase } from '../utils/formatters';
import './patient-logV2.scss';

type ActivityFeedItemType<TypeName, TypeDef = null> = {
  id: string,
  timestamp: DateTime,
  type: TypeName,
  data: TypeDef,
};

export type ActivityFeedItemMealLog = ActivityFeedItemType<'meal-log', PatientActivityFeedRowMealLog['data']>;
export type ActivityFeedItemExerciseLog = ActivityFeedItemType<
  'exercise-log',
  PatientActivityFeedRowExerciseLog['data']
>;
export type ActivityFeedItemMedicationLog = ActivityFeedItemType<
  'medication-log',
  PatientActivityFeedRowMedicationLog['data']
>;

export type ActivityFeedItemCGMSummary = ActivityFeedItemType<'cgm-summary'>;
export type ActivityFeedItemWaterLog = ActivityFeedItemType<'water-log'>;
export type ActivityFeedItemMoodButton = ActivityFeedItemType<'mood-button'>;
export type ActivityFeedItemMissingMeal = ActivityFeedItemType<'missing-meal', {
  meal_name: MealName,
}>;
export type ActivityFeedItemEmotionLog = ActivityFeedItemType<'emotion-log', PatientEmotionResponse>;
export type ActivityFeedItemEmotionalHealthLog = ActivityFeedItemType<
  'emotional-health-log',
  PatientEmotionalHealthResponse
>;
export type ActivityFeedItemMindfulnessLog = ActivityFeedItemType<'mindfulness-log', PatientMindfulnessResponse>;
export type ActivityFeedItemSleepLog = ActivityFeedItemType<'sleep-log', PatientSleepDataResponse>;
export type ActivityFeedItemStressLog = ActivityFeedItemType<'stress-log', PatientStressResponse>;
export type ActivityFeedItemStepsLog = ActivityFeedItemType<'steps-log', PatientStepsDataResponse>;

export type ActivityFeedItem =
  | ActivityFeedItemMealLog
  | ActivityFeedItemExerciseLog
  | ActivityFeedItemMedicationLog
  | ActivityFeedItemCGMSummary
  | ActivityFeedItemWaterLog
  | ActivityFeedItemMoodButton
  | ActivityFeedItemMissingMeal
  | ActivityFeedItemEmotionLog
  | ActivityFeedItemEmotionalHealthLog
  | ActivityFeedItemMindfulnessLog
  | ActivityFeedItemSleepLog
  | ActivityFeedItemStressLog
  | ActivityFeedItemStepsLog;

export type ActivityFeedSection = {
  id: string,
  date: DateTime,
  data: ActivityFeedItem[],
};

export const FILTER_OPTIONS = (t: TFunction) =>
  ({
    mealType: {
      label: t('Meal type'),
      options: [
        {
          'value': 'breakfast',
          'label': t('Breakfast'),
        },
        {
          'value': 'lunch',
          'label': t('Lunch'),
        },
        {
          'value': 'dinner',
          'label': t('Dinner'),
        },
        {
          'value': 'snack',
          'label': t('Snack'),
        },
      ],
    },
    nutrition: {
      label: t('Nutrition'),
      options: [
        // negative nutrition values commented out for now
        {
          'value': 'healthy_fat',
          'label': t('Healthy fat'),
        },
        // {
        // 	"value": "unhealthy_fat",
        // 	"label": "Unhealthy fat"
        // },
        {
          'value': 'plant_based',
          'label': t('Plant-based protein'),
        },
        {
          'value': 'ultraprocessed',
          'label': t('Processed foods'),
        },
        {
          'value': 'omega_three',
          'label': t('Omega-3'),
        },
        {
          'value': 'refined_grains',
          'label': t('Refined grains'),
        },
        {
          'value': 'whole',
          'label': t('Whole grains'),
        },
        {
          'value': 'high_fiber',
          'label': t('Fibre'),
        },
        {
          'value': 'sugary_beverage',
          'label': t('Sugary drinks'),
        },
        {
          'value': 'redmeat',
          'label': t('Red meat'),
        },
        {
          'value': 'nuts_seeds',
          'label': t('Nuts and seeds'),
        },
        {
          'value': 'legume',
          'label': t('Legumes'),
        },
        {
          'value': 'vegetable',
          'label': t('Vegetables'),
        },
        {
          'value': 'fruit',
          'label': t('Fruits'),
        },
      ],
    },
    glucose: {
      // hidden: !user?.flags?.patient_show_dexcom_oauth,
      label: t('Glucose'),
      options: [
        {
          'value': 'glucose_response_low',
          'label': t('Low'),
          'cardLabel': t('Low glucose'),
        },
        {
          'value': 'glucose_response_high',
          'label': t('High'),
          'cardLabel': t('High glucose'),
        },
        {
          'value': 'glucose_response_in_range',
          'label': t('In-range'),
          'cardLabel': t('In-range glucose'),
        },
        {
          'value': 'glucose_response_unknown',
          'label': t('Unknown/missing'),
          'cardLabel': t('Unknown/missing glucose'),
        },
      ],
    },
    logType: {
      label: t('Log Type'),
      options: [
        {
          'value': 'exercise-log',
          'label': t('Exercise'),
          'cardLabel': t('Exercise log'),
        },
        {
          'value': 'medication-log',
          'label': t('Medication'),
          'cardLabel': t('Medication log'),
        },
        {
          'value': 'meal-log',
          'label': t('Meal'),
          'cardLabel': t('Meal log'),
        },
        {
          'value': 'has_mindful_eating_survey',
          'label': t('Mindful eating'),
          'cardLabel': t('Mindful eating'),
        },
        {
          'value': 'steps-log',
          'label': t('Steps'),
          'cardLabel': t('Steps'),
        },
        {
          'value': 'sleep-log',
          'label': t('Sleep'),
          'cardLabel': t('Sleep'),
        },
      ],
    },
  }) as const;

type ApiTableFilterVal = string | number | boolean | null | undefined;

type ApiTableFilter<T = ApiTableFilterVal> =
  | T
  | Partial<{
    lt: T,
    lte: T,
    gt: T,
    gte: T,
    eq: T,
    neq: T,
  }>;

export type ActivityFeedFilterOptions = Partial<ReturnType<typeof FILTER_OPTIONS>>;

type ActivityPage = {
  activities: ActivityFeedItem[],
  earliestDay: DateTime,
  latestDay: DateTime,
  pagination: {
    total: number,
    hasNext: boolean,
    nextCursor: DateTime,
  },
};

export const useMealCache = create<Record<string, MealResponse>>(() => ({}));

export const usePatientActivityFeed = (opts: {
  patientId: number,
  filterOptions?: SelectedOptionsType,
  filter?: Partial<{ timestamp: ApiTableFilter }>,
  activeDate?: DateTime | null,
  enabled?: boolean | true,
}) => {
  const { filterOptions, filter, activeDate, patientId, enabled } = opts;

  const mealCache = useMealCache();
  const { i18n } = useTranslation();
  const query = useInfiniteQuery<ActivityPage>({
    queryKey: ['meal-timeline', patientId, filterOptions, filter, activeDate],
    getNextPageParam: (lastPage, pages) => {
      if (!lastPage.pagination.hasNext) {
        return;
      }
      return lastPage.pagination;
    },
    queryFn: async (context): Promise<ActivityPage> => {
      const pageParam: ActivityPage['pagination'] = context.pageParam;
      const before: DateTime | boolean = pageParam
        ? pageParam.hasNext && pageParam.nextCursor
        : activeDate
        ? activeDate
        : DateTime.now();

      if (!before) {
        return {
          activities: [],
          earliestDay: null,
          latestDay: null,
          pagination: {
            total: 0,
            nextCursor: null,
            hasNext: false,
          },
        };
      }

      const getFilterValues = (filterType: keyof ActivityFeedFilterOptions, prependCc?: string) => {
        return Object.entries(filterOptions?.[filterType] || {})
          .filter(([_, value]) => value)
          .map(([key, _]) => prependCc ? prependCc + key : key);
      };

      const mealFilter: Record<string, boolean | Record<'in', MealName[]>> = {};
      const logTypeFilter: Record<string, ActivityFeedItem['type'][]> = {};

      getFilterValues('logType').map(type => {
        if (type == 'has_mindful_eating_survey') {
          mealFilter[type] = true;
        } else {
          logTypeFilter['or'] = logTypeFilter['or'] || [];
          logTypeFilter['or'].push(type as ActivityFeedItem['type']);
        }
      });
      getFilterValues('mealType').map(meal => {
        mealFilter['meal_name'] = mealFilter['meal_name'] || { in: [] };
        mealFilter['meal_name']['in'].push(meal);
      });
      getFilterValues('nutrition', 'cc_').map(flag => mealFilter[flag] = true);
      getFilterValues('glucose').map(flag => mealFilter[flag] = true);

      const apiFilter: Record<string, any> = {
        'meal-log': mealFilter,
        'type': logTypeFilter,
        ...(filter || {}),
      };

      // For pagination, narrow the timestamp upper bound to the cursor
      // so the API returns the next batch of earlier items
      if (pageParam && apiFilter.timestamp) {
        apiFilter.timestamp = { ...apiFilter.timestamp, lt: before.toISODate() };
      }

      const res = await patientApi.appApiPatientActivityFeedGetPatientActivityFeed({
        patient_id: patientId,
        filter: JSON.stringify(apiFilter),
        before: (pageParam ? before : before.plus({ days: 1 })).toISODate(),
        limit: 100,
      });

      // Update cache with fresh data from API, don't delete existing entries
      const newCache = { ...mealCache };
      res.data.rows.forEach((row) => {
        if (row.type == 'meal-log') {
          newCache[row.data.id] = row.data;
        }
      });
      useMealCache.setState(newCache);

      const wrappedActivities = res.data.rows.map((item) => ({
        ...item,
        timestamp: parseLocalDateTimeLux(item.timestamp),
      }));

      /*
      if (config.IS_LOCAL || config.IS_DEV) {
        const firstDate = before;
        wrappedActivities.push({
          id: `debug-exercise-${firstDate.toMillis()}`,
          type: 'exercise-log',
          timestamp: firstDate.set({ hour: 13, minute: 0, second: 0, millisecond: 0 }),
          data: {
            id: firstDate.toMillis(),
            duration_min: 30,
            exercise_date: firstDate.toISODate(),
            exercise_time: '13:00:00',
            exercise_type: 'aerobic training',
            intensity: 'medium',
          },
        });
        wrappedActivities.push({
          id: `debug-medication-${firstDate.toMillis() + 1}`,
          type: 'medication-log',
          timestamp: firstDate.set({ hour: 11, minute: 45, second: 0, millisecond: 0 }),
          data: {
            id: firstDate.toMillis() + 1,
            medication_date: firstDate.toISODate(),
            medication_time: '11:45:00',
            name: 'insulin',
            quantity: 3,
            unit: 'units',
          },
        });
      }
      */

      wrappedActivities.sort((a, b) => b.timestamp.toMillis() - a.timestamp.toMillis());
      const earliestDay = before;
      const oldestActivity = wrappedActivities[wrappedActivities.length - 1];
      const latestDay = oldestActivity?.timestamp.endOf('day');

      return {
        activities: wrappedActivities,
        earliestDay,
        latestDay,
        pagination: {
          total: res.data.rows.length,
          nextCursor: oldestActivity?.timestamp.startOf('day'),
          hasNext: !!res.data.has_more,
        },
      };
    },
    enabled,
  });

  const feedEntries = useMemo(() => {
    if (!query.data) {
      return [];
    }

    const latestDay = query.data.pages[0].earliestDay;

    const sectionsObj = {} as Record<
      string, // date string
      ActivityFeedSection
    >;
    const getOrInsertSection = (day: DateTime): ActivityFeedSection => {
      if (!sectionsObj[day.toISODate()]) {
        sectionsObj[day.toISODate()] = {
          id: `${day.toISODate()}-${Math.random().toString()}`,
          date: day,
          data: [],
        };
      }
      return sectionsObj[day.toISODate()];
    };

    // go from latestDay to earliestDay
    for (let offset = 0; offset < 5; offset++) {
      // Insert empty sections for the first 5 days
      getOrInsertSection(latestDay.minus({ days: offset }));
    }

    // Use Set for better performance and clarity
    const seenMealIds = new Set<string>();
    const processedMeals = new Map<string, ActivityFeedItemMealLog>();

    // First pass: collect all meals from API response and cache
    // This prevents duplication when meals exist in both API response and cache
    query.data.pages.forEach((page) => {
      page.activities.forEach((activity) => {
        if (activity.type != 'meal-log') {
          const section = getOrInsertSection(activity.timestamp);
          section.data.push(activity);
          return;
        }

        const mealId = activity.data.id.toString();
        const cached = mealCache[mealId];

        // Skip deleted meals
        if (cached?.is_deleted) {
          return;
        }

        // Use cached version if available (cached data is more up-to-date)
        const activityToAdd = cached
          ? { ...activity, data: cached }
          : activity;

        // Update timestamp if using cached data
        if (activityToAdd !== activity) {
          activityToAdd.timestamp = parseLocalDateTimeLux(activityToAdd.data.meal_date, activityToAdd.data.meal_time);
        }

        // Store the meal with the most up-to-date data
        processedMeals.set(mealId, activityToAdd);
        seenMealIds.add(mealId);
      });
    });

    // Second pass: add cached meals that are not already processed and belong to the current patient
    // if (mealCache && typeof mealCache === 'object') {
    //   Object.values(mealCache).forEach((meal) => {
    //     // Type guard to ensure meal is a MealResponse
    //     if (!meal || typeof meal !== 'object' || !('id' in meal)) {
    //       return;
    //     }

    //     const mealResponse = meal as MealResponse;
    //     const mealId = mealResponse.id.toString();

    //     // Skip if already processed, deleted, or wrong patient
    //     if (
    //       seenMealIds.has(mealId)
    //       || mealResponse.is_deleted
    //       || patientId !== mealResponse.patient_id
    //     ) {
    //       return;
    //     }

    //     const activity: ActivityFeedItemMealLog = {
    //       id: `meal-log:${mealResponse.id}`,
    //       type: 'meal-log',
    //       timestamp: parseLocalDateTimeLux(mealResponse.meal_date, mealResponse.meal_time),
    //       data: mealResponse,
    //     };

    //     processedMeals.set(mealId, activity);
    //     seenMealIds.add(mealId);
    //   });
    // }

    // Third pass: add all processed meals to their respective sections
    // This ensures each meal appears only once in the final result
    processedMeals.forEach((activity) => {
      const section = getOrInsertSection(activity.timestamp);
      section.data.push(activity);
    });

    const sections = Object.values(sectionsObj);

    sections.forEach((section) => {
      section.data.sort((a, b) => {
        return b.timestamp.toMillis() - a.timestamp.toMillis();
      });
    });

    // Sort sections by date (newest first) to ensure proper ordering
    sections.sort((a, b) => b.date.toMillis() - a.date.toMillis());

    return sections.filter(s => s.data.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data, mealCache, patientId]);

  const filteredFeedEntries = useMemo(() => {
    return feedEntries.filter((section) => section.data.length > 0);
  }, [feedEntries]);

  return {
    query,
    feedEntries,
    filteredFeedEntries,
  };
};

export const activityFeedItemsToCgmSummaryChartPoints = (
  t: TFunc,
  activityData: ActivityFeedItem[],
): CGMReportMarkerPoint[] => {
  if (!activityData?.length) {
    return [];
  }
  const defs = getActivityFeedDefs(t);

  return activityData.map((item, i) => {
    const def = defs[item.type];
    if (!def) {
      return null;
    }

    const res: CGMReportMarkerPoint = {
      icon: def.iconImage,
      color: def.color,
      xTimestampMS: item.timestamp.toJSDate(),
    };
    return res;
  }).filter(Boolean) as CGMReportMarkerPoint[];
};

const CgmSummaryItem = (props: {
  timestamp: DateTime,
  activityData: ActivityFeedItem[],
}) => {
  const { t } = useTranslation();
  const { patient } = useStore();
  const date = props.timestamp;
  const viewDates = {
    patientId: patient.patient_id,
    timeSinceLocal: date.startOf('day').toJSDate(),
    timeUntilLocal: date.endOf('day').toJSDate(),
  };

  // const cgmRefetch = useCgmRefetch(); TODO
  const cgm = useCgmData(viewDates);
  const cgmData = useProcessedCgmData(cgm.glucoseData);

  const width = 240;

  const result = useMemo(() => {
    if (!cgmData || cgmData.length === 0) {
      return null;
    }

    const markers = activityFeedItemsToCgmSummaryChartPoints(t, props.activityData);
    return cgmData.length > 0
      ? (
        <CGMGlucoseChart
          data={cgmData}
          markers={markers}
          height={110}
          width={width}
          showValueAxis={true}
          showTimeAxis={true}
          xTickFrequencyHours={3}
          xTickFirstOffsetHours={3}
          maxXTicks={12}
          xDomainMS={[viewDates.timeSinceLocal.getTime(), viewDates.timeUntilLocal.getTime()]}
          showTargetRange={true}
        />
      )
      : (
        <p>
          <Trans>No data available</Trans>
        </p>
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cgmData,
    props.activityData,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    viewDates.timeSinceLocal.toISOString(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    viewDates.timeUntilLocal.toISOString(),
    width,
  ]);

  return result;
};

const MacroCardLargeStat = (
  props: {
    value: string | null,
    label: string,
    unit: string,
    color?: string,
    labelSize?: string,
    size?: string,
    showLabel?: boolean,
  },
) => {
  return (
    <div>
      {props.showLabel !== false && props.label && (
        <span
          className="text-uppercase d-block"
          style={{ fontSize: props.labelSize || props.size || '10px', color: props.color, height: '30px' }}
        >
          <Trans>{props.label}</Trans>
        </span>
      )}
      <h5 className="mb-0" style={{ color: props.color, fontSize: props.size || '8px' }}>
        {typeof props.value === 'string' ? props.value + props.unit : '-'}
      </h5>
    </div>
  );
};

export const MACRO_NUTRIENT_QUERY_KEY = 'patient-nutritional-report';
export const MacroNutrientSummaryCard = (props: {
  summaryDate: DateTime,
  mealId?: number,
  meal?: ActivityFeedItem[] | MealResponse,
}) => {
  const { summaryDate } = props;
  const { patient } = useStore();
  const { t, i18n } = useTranslation();

  const nutrientQuery = useQuery(
    [MACRO_NUTRIENT_QUERY_KEY, summaryDate, props.mealId],
    async () => {
      const res = await reportingApi.appApiPatientReportsApiGetPatientNutritionDetailsTable({
        patient_id: patient.patient_id,
        group_by: 'meal_name',
        filter: JSON.stringify({
          meal_date: summaryDate,
          meal_id: props.mealId || undefined,
        }),
      });
      return res.data;
    },
    {
      refetchOnMount: false,
      staleTime: 1000 * 60,
    },
  );

  // Use all rows from the query
  const rows = nutrientQuery.data?.rows || [];

  // Group rows by meal_name (which is one of "breakfast", "lunch", "dinner", "snack")
  const groupedByMeal = _.groupBy(rows, row => row.meal_name?.toLowerCase());

  // Helper function to sum nutrient values for a group (using the "_sum" fields)
  const getNutrientSum = (group: PatientNutritionDetailsTableRow[], nutrient: string) =>
    _.sum((group || []).map(row => {
      // eslint-disable-next-line i18next/no-literal-string
      const nutrientKey = `${nutrient}_sum`; // Declare the computed key as a variable
      // console.log('nutrientKey', nutrientKey);
      // Return the nutrient value for this row (or 0 if it's not a number)
      return Number(row[nutrientKey]) || 0;
    }));

  const nutrients: (keyof PatientNutritionDetailsTableRow)[] = [
    'protein_g',
    'fat_g',
    'fiber_g',
    'energy_kcal',
    'carbohydrate_g',
    'netcarb_g',
  ];

  // const carbLabel = patientFlags.flags.patient_show_total_carbs ? labelDefs.carbohydrate_g : labelDefs.netcarb_g;
  // const carbField = patientFlags.flags.patient_show_total_carbs ? 'carbohydrate_g_sum' : 'netcarb_g_sum';

  const mealHasOverrides = props.meal && (Array.isArray(props.meal)
    ? props.meal?.some(item =>
      item.type == 'meal-log'
      && (item.data.meal_items?.some(mi => mi.custom_addons?.some(ca => ca.nutrient_overrides?.aa_patient))
        || item.data.meal_items?.some(mi => mi.nutrient_overrides?.aa_patient))
    )
    : props.meal?.meal_items?.some(mi => mi.custom_addons?.some(ca => ca.nutrient_overrides?.aa_patient))
      || props.meal?.meal_items?.some(mi => mi.nutrient_overrides?.aa_patient));

  const labelDefs = getNutrientLabelDefs(t);

  // Define the meal names you want to show
  // eslint-disable-next-line i18next/no-literal-string
  const meals = ['breakfast', 'lunch', 'dinner', 'snack'];

  // Style for detailed rows with a smaller font
  const detailFontStyle = { fontSize: '0.5rem' };
  const mealHeadingStyle = { fontSize: '0.9rem', marginBottom: '0' };

  const totalLabel = t('Total');

  return (
    <div>
      {/* Total nutrient values over all meals (moved to top) */}
      <div style={{ marginBottom: '0.5rem' }}>
        <div className="d-flex align-items-center">
          {/* Left column: fixed width for consistent total label alignment */}
          <div style={{ width: '50px', textAlign: 'left', marginTop: '2rem' }}>
            <h4 style={{ fontSize: '1.1rem', marginBottom: '0' }}>{totalLabel}</h4>
          </div>
          {/* Right column: container for MacroCardLargeStat items */}
          <div className="d-flex flex-grow-1" style={{ marginBottom: '0', marginTop: '0' }}>
            {nutrients.map(nutrient => {
              // eslint-disable-next-line i18next/no-literal-string
              const nutrientKey = `${nutrient}_sum`;
              // Sum the nutrient value over all rows
              const totalSum = _.sum(
                rows.map(row => Number(row[nutrientKey]) || 0),
              );
              const formattedTotal = formatNumber(i18n.language, totalSum, 0);
              return (
                // Each stat gets equal width and right-aligned content
                <div key={nutrient} style={{ flex: 1, textAlign: 'right' }}>
                  <MacroCardLargeStat
                    value={formattedTotal}
                    label={labelDefs[nutrient].labelTr}
                    unit={labelDefs[nutrient].unitSuffix}
                    color={labelDefs[nutrient].colorize(formattedTotal)}
                    size="1.1rem"
                    labelSize="0.7rem"
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Render each meal's details */}
      {!props.mealId && meals.map(meal => {
        // Get the nutrient rows for this meal (if none, an empty array)
        const group = groupedByMeal[meal] || [];

        return (
          <div key={meal}>
            <div className="d-flex align-items-center" style={{ marginTop: '0.2rem' }}>
              {/* Left column: fixed width for consistent meal heading alignment */}
              <div style={{ width: '50px', textAlign: 'left', marginTop: '0' }}>
                <h6 style={mealHeadingStyle}>
                  {t(meal).charAt(0).toUpperCase() + t(meal).slice(1)}
                </h6>
              </div>
              {/* Right column: container for MacroCardLargeStat items */}
              <div className="d-flex flex-grow-1" style={{ marginBottom: '0', marginTop: '0' }}>
                {nutrients.map(nutrient => {
                  const sum = getNutrientSum(group, nutrient);
                  const formattedVal = formatNumber(i18n.language, sum, 0);
                  return (
                    // Each stat gets equal width and right-aligned content
                    <div key={nutrient} style={{ flex: 1, textAlign: 'right' }}>
                      <MacroCardLargeStat
                        value={formattedVal}
                        label={labelDefs[nutrient].labelTr}
                        unit={labelDefs[nutrient].unitSuffix}
                        color={labelDefs[nutrient].colorize(formattedVal)}
                        size="0.9rem"
                        showLabel={false}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}

      {mealHasOverrides && (
        <div
          style={{
            display: 'flex',
            gap: '8px',
            fontStyle: 'italic',
            marginTop: '10px',
            color: '#666666',
          }}
        >
          <div style={{ fontWeight: 'bold' }}>
            <Trans>!</Trans>
          </div>
          <Trans>
            The patient has customized these macronutrient values.
          </Trans>
        </div>
      )}
    </div>
  );
};

type ActivityFeedTypeDef = {
  titleTr: string,
  iconImage: string,
  color: string,
};

export const getActivityFeedDefs = (t: TFunction): Record<ActivityFeedItem['type'], ActivityFeedTypeDef | null> => ({
  'meal-log': {
    iconImage: MealImage,
    titleTr: t('Meal'),
    color: '#36C2B4',
  },
  'exercise-log': {
    iconImage: ExerciseImage,
    titleTr: t('Exercise'),
    color: '#ff9161',
  },
  'medication-log': {
    iconImage: InsulinImage,
    titleTr: t('Medication'),
    color: '#878ef8',
  },
  'emotion-log': {
    iconImage: EmotionImage,
    titleTr: t('Emotion'),
    color: '#FFDC00',
  },
  'cgm-summary': null,
  'water-log': null,
  'mood-button': null,
  'missing-meal': null,
  'emotional-health-log': null,
  'mindfulness-log': null,
  'sleep-log': null,
  'stress-log': null,
  'steps-log': null,
});

type PatientActivityLogActivityGroup = {
  id: string,
  groupType: string,
  def: ActivityFeedTypeDef,
  items: ActivityFeedItem[],
};

const getNewOptions = (
  searchOptions: SelectedOptionsType,
  optionCategory: keyof ActivityFeedFilterOptions,
  key: FilterOptionsType<typeof optionCategory>,
) => {
  const toggleMealLogTrue = optionCategory !== 'logType' || key === 'has_mindful_eating_survey';
  const newOptions = {
    ...searchOptions,
    logType: {
      ...searchOptions['logType'],
      'meal-log': toggleMealLogTrue || searchOptions['logType']?.['meal-log'],
    },
  };
  newOptions[optionCategory] = { ...newOptions[optionCategory], [key]: !newOptions[optionCategory]?.[key] };
  if (key === 'meal-log') {
    return newOptions.logType['meal-log']
      ? newOptions
      : { logType: { ...newOptions.logType, has_mindful_eating_survey: false } };
  }

  if (newOptions[optionCategory][key] === false) {
    delete newOptions[optionCategory][key];
  }
  if (!Object.keys(newOptions[optionCategory]).length) {
    delete newOptions[optionCategory];
  }
  if (
    newOptions.logType['meal-log'] && Object.keys(newOptions.logType).length === 1
    && Object.keys(newOptions).length === 1
  ) {
    delete newOptions.logType['meal-log'];
  }
  return newOptions;
};

const PatientActivityLogDayView = (props: {
  section: ActivityFeedSection,
  openAddMealPanel: () => void,
  onClickItem: (item: ActivityFeedItem) => void,
}) => {
  const { patient, clinician } = useStore();
  const { tAttr, t, i18n } = useTranslation();
  const meal = props.section;
  const patientFlags = useCurrentPatientData();
  const recentlyModifiedMeal = useRecentlyModifiedMeal().recentlyModifiedMeal;

  const groupedEntries: PatientActivityLogActivityGroup[] = React.useMemo(() => {
    const res: PatientActivityLogActivityGroup[] = [];
    const typeDefs = getActivityFeedDefs(t);

    _.sortBy(props.section.data, 'timestamp').forEach((item) => {
      const def = typeDefs[item.type];
      if (!def) {
        return;
      }
      const lastGroup = res[res.length - 1];
      // eslint-disable-next-line i18next/no-literal-string
      const groupType = item.type == 'meal-log' ? `meal-log:${item.data.meal_name}` : item.type;
      if (lastGroup?.groupType == groupType) {
        lastGroup.items.push(item);
      } else {
        res.push({
          id: `${groupType}:${item.id}`,
          groupType: groupType,
          def: {
            ...def,
            titleTr: item.type == 'meal-log' ? titleCase(t(item.data.meal_name)) : def.titleTr,
          },
          items: [item],
        });
      }
    });

    return res;
  }, [props.section, t]);

  const dayStart = meal.date.startOf('day');
  const dayEnd = meal.date.startOf('day').plus({ days: 1 });

  const exerciseLogs = props.section.data
    .filter(item => item.type === 'exercise-log')
    .map(item => ({
      timestamp: item.timestamp,
      duration_min: item.data.duration_min,
      intensity: item.data.intensity,
    }));

  const insulinLogs = props.section.data
    .filter(item => item.type === 'medication-log')
    .map(item => ({
      timestamp: item.timestamp,
      dosage: item.data.quantity,
    }));
  const maxInsulinDosage = Math.max(...insulinLogs.map(il => il.dosage));

  const mealLogs = props.section.data
    .filter(item => item.type === 'meal-log')
    .map(item => ({
      meal_id: item.data.id,
      timestamp: item.timestamp,
      meal_name: item.data.meal_name,
      meal_items: item.data.meal_items.map(mi => tAttr(mi, 'food_name')),
    }));

  const sleepLogs = props.section.data
    .filter(item => item.type === 'sleep-log')
    .map(item => ({
      duration_min: item.data.sleep_duration_min,
    }));
  const sleepTotal = (sleepLogs.reduce((acc, curr) => acc + curr.duration_min, 0) ?? 0) / 60;
  const sleepHrs = String(Math.round(sleepTotal * 10) / 10);
  const sleepStr = t('Sleep: {{ sleepHrs }} hrs', { sleepHrs });

  const stepsLogs = props.section.data
    .filter(item => item.type === 'steps-log')
    .map(item => ({
      step_count: item.data.step_count,
    }));
  const stepsTotal = stepsLogs.reduce((acc, curr) => acc + curr.step_count, 0) ?? 0;
  const stepsStr = t('Steps: {{ stepsTotal }}', { stepsTotal: formatNumber(i18n.language, stepsTotal, 0) });

  const noDataStyle = { display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' };

  // Debug: add `?debugSleepChart` to the URL to print the sleep chart rows/segments/model to the console.
  // eslint-disable-next-line i18next/no-literal-string
  const sleepChartDebug = window.location.search.includes('debugSleepChart');
  const sleepChartDataQuery = useSleepChartData({ date: meal.date.toISODate(), enabled: true });
  const sleepChartSegments = React.useMemo(
    () => getSleepSegments(sleepChartDataQuery.data).filter((s) => s.type !== 'inBed'),
    [sleepChartDataQuery.data],
  );
  const formatMinutesHM = React.useCallback(
    (mins: number) => {
      const totalMinutes = Math.max(0, Math.round(mins));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      if (hours > 0 && minutes > 0) {
        return t('{{hours}}h {{minutes}}m', { hours, minutes });
      }
      if (hours > 0) {
        return t('{{hours}}h', { hours });
      }
      return t('{{minutes}}m', { minutes });
    },
    [t],
  );
  const sleepTotalFromStagesMin = React.useMemo(() => {
    const segments = getSleepSegments(sleepChartDataQuery.data);
    return segments
      .filter((s) => s.type !== 'awake' && s.type !== 'inBed')
      .reduce((acc, curr) => acc + (curr.to.getTime() - curr.from.getTime()) / (1000 * 60), 0);
  }, [sleepChartDataQuery.data]);
  const sleepStageTotals = React.useMemo(() => {
    const totals: Partial<Record<SleepStageType, number>> = {};
    const segments = getSleepSegments(sleepChartDataQuery.data);
    segments.forEach((seg) => {
      if (seg.type === 'awake' || seg.type === 'inBed') {
        return;
      }
      const mins = (seg.to.getTime() - seg.from.getTime()) / (1000 * 60);
      totals[seg.type] = (totals[seg.type] ?? 0) + mins;
    });
    return totals;
  }, [sleepChartDataQuery.data]);
  const sleepStageBreakdown = React.useMemo(() => {
    return (Object.entries(sleepStageTotals) as Array<[SleepStageType, number]>)
      .filter(([_, mins]) => mins > 0)
      .sort((a, b) => SleepStage[a[0]].position - SleepStage[b[0]].position);
  }, [sleepStageTotals]);
  const getSleepStageLabel = React.useCallback(
    (type: SleepStageType) => {
      switch (type) {
        case 'awake':
          return t('Awake');
        case 'rem':
          return t('REM');
        case 'core':
          return t('Core');
        case 'deep':
          return t('Deep');
        case 'asleepUnspecified':
          return t('Asleep');
        case 'inBed':
          return t('In Bed');
        default:
          return '';
      }
    },
    [t],
  );

  const heartRate = useHealthDataTable({
    enabled: true,
    grouper: 'hour',
    category: 'heart_rate',
    aggregator: ['p25', 'p50', 'p75'],
    field: 'heart_rate_bpm',
    timestampFilter: {
      'gte': meal.date.toISODate(),
      'lt': meal.date.plus({ days: 1 }).toISODate(),
    },
  });
  const oxygenSat = useHealthDataTable({
    enabled: true,
    grouper: 'hour',
    category: 'spo2',
    aggregator: ['p25', 'p50', 'p75'],
    field: 'spo2_pct',
    timestampFilter: {
      'gte': meal.date.toISODate(),
      'lt': meal.date.plus({ days: 1 }).toISODate(),
    },
  });

  const heartRateData = useProcessedHeartRateData(heartRate.data);
  const spo2Data = useProcessedOxygenData(oxygenSat.data);

  return (
    <div className="timeline-day">
      <div className="timeline-header">
        <Row>
          <Col className="timeline-item-title d-flex align-items-center">
            <div className="d-flex date-wrapper align-items-center">
              <h4 className="m-0" style={{ whiteSpace: 'nowrap' }}>{formatDateLongMonth(i18n.language, meal.date)}</h4>
              {/* eslint-disable-next-line i18next/no-literal-string*/}
              <span>&bull;</span>
              <h5 className="text-muted m-0">
                {meal.date?.setLocale(i18n.language).weekdayLong}
              </h5>
              {/* eslint-disable-next-line i18next/no-literal-string */}
              <span>&bull;</span>
            </div>
            <div className="d-none d-md-block">
              <WaterLog date={meal.date} patientId={patient.patient_id} />
            </div>
          </Col>
        </Row>
      </div>
      <div className="timeline-content">
        <Row>
          <Col xs={4}>
            <div className="day-summary">
              {patientFlags.flags.patient_show_dexcom_oauth && (
                <>
                  <h5>
                    <Trans>Glucose profile</Trans>
                  </h5>
                  <div className="CGMgraphContainer">
                    <CgmSummaryItem
                      timestamp={props.section.date}
                      activityData={props.section.data}
                    />
                  </div>
                </>
              )}
              {mealLogs.length > 0
                ? (
                  <>
                    <h5>
                      <Trans>Meal Composition</Trans>
                    </h5>
                    <div className="MealCompositionChartContainer">
                      <MealCompositionChart
                        width={240}
                        height={120}
                        xDomainMS={[dayStart.toMillis(), dayEnd.toMillis()]}
                        xTickFrequencyHours={3}
                        date={meal.date}
                        mealLogs={mealLogs}
                      />
                    </div>
                  </>
                )
                : (
                  <div style={noDataStyle}>
                    <h5 style={{ margin: 0 }}>
                      <Trans>Meal Composition</Trans>
                    </h5>
                    <p style={{ margin: 0 }}>
                      <Trans>No data available</Trans>
                    </p>
                  </div>
                )}
              {
                // eslint-disable-next-line i18next/no-literal-string
                patientFlags.flags?.patient_app_extended_logging_types?.includes('insulin')
                  && insulinLogs.length > 0
                  && maxInsulinDosage > 0
                  ? (
                    <>
                      <h5>
                        <Trans>Insulin</Trans>
                      </h5>
                      <div className="InsulinChartContainer">
                        <InsulinChart
                          width={240}
                          height={75}
                          xDomainMS={[dayStart.toMillis(), dayEnd.toMillis()]}
                          xTickFrequencyHours={3}
                          insulinLogs={insulinLogs}
                        />
                      </div>
                    </>
                  )
                  : (
                    <div style={noDataStyle}>
                      <h5 style={{ margin: 0 }}>
                        <Trans>Insulin</Trans>
                      </h5>
                      <p style={{ margin: 0 }}>
                        <Trans>No data available</Trans>
                      </p>
                    </div>
                  )
              }
              {
                // eslint-disable-next-line i18next/no-literal-string
                patientFlags.flags?.patient_app_extended_logging_types?.includes('exercise')
                  && exerciseLogs.length > 0
                  ? (
                    <>
                      <h5>
                        <Trans>Exercise</Trans>
                      </h5>
                      <div className="ExerciseChartContainer">
                        <ExerciseChart
                          width={240}
                          height={55}
                          xDomainMS={[dayStart.toMillis(), dayEnd.toMillis()]}
                          xTickFrequencyHours={3}
                          exerciseLogs={exerciseLogs}
                        />
                      </div>
                    </>
                  )
                  : (
                    <div style={noDataStyle}>
                      <h5 style={{ margin: 0 }}>
                        <Trans>Exercise</Trans>
                      </h5>
                      <p style={{ margin: 0 }}>
                        <Trans>No data available</Trans>
                      </p>
                    </div>
                  )
              }
              {heartRateData.length
                ? (
                  <>
                    <h5>
                      {spo2Data.length > 0 ? <Trans>Heart Rate & SpO2</Trans> : <Trans>Heart Rate</Trans>}
                    </h5>
                    <div className="HeartRateChartContainer">
                      <HeartRateChart
                        xDomainMS={[dayStart.toMillis(), dayEnd.toMillis()]}
                        xTickFrequencyHours={3}
                        height={120}
                        width={240}
                        heartRateData={heartRateData}
                        spo2Data={spo2Data}
                      />
                    </div>
                  </>
                )
                : null}
              {
                /* (
                   <div style={noDataStyle}>
                     <h5 style={{ margin: 0 }}>
                       <Trans>Heart Rate & SpO2</Trans>
                     </h5>
                     <p style={{ margin: 0 }}>
                       <Trans>No data available</Trans>
                     </p>
                   </div>
                 )} */
              }
              {
                // eslint-disable-next-line i18next/no-literal-string
                (patientFlags.flags?.patient_app_extended_logging_types?.includes('sleep'))
                  ? sleepChartDataQuery.query.isLoading
                    ? (
                      <div style={noDataStyle}>
                        <h5 style={{ margin: 0 }}>
                          <Trans>Sleep</Trans>
                        </h5>
                        <LoadingSpinner />
                      </div>
                    )
                    : sleepChartSegments.length > 0
                    ? (
                      <div style={{ marginBottom: '10px' }}>
                        <h5 style={{ marginBottom: '6px' }}>
                          <Trans>Sleep</Trans>
                        </h5>
                        <div style={{ marginBottom: '6px' }}>
                          <div>
                            {t('Total asleep time:  {{time}}', { time: formatMinutesHM(sleepTotalFromStagesMin) })}
                          </div>
                          {sleepStageBreakdown.length > 1 && (
                            <div style={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: '2px' }}>
                              <div>
                                {t('Sleep stages:')}
                              </div>
                              {sleepStageBreakdown.map(([type, mins], idx) => (
                                <React.Fragment key={type}>
                                  <div style={{ marginLeft: '6px', fontSize: '0.89rem' }}>
                                    {t('{{stage}} total:  {{time}}', {
                                      stage: getSleepStageLabel(type),
                                      time: formatMinutesHM(mins),
                                    })}
                                  </div>
                                </React.Fragment>
                              ))}
                            </div>
                          )}
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                          <div style={{ minWidth: 480 }}>
                            <SleepStagesChart
                              data={sleepChartDataQuery.data}
                              width={480}
                              height={180}
                              debug={sleepChartDebug}
                            />
                          </div>
                        </div>
                      </div>
                    )
                    : sleepLogs.length > 0
                    ? (
                      <div>
                        <h5>{sleepStr}</h5>
                      </div>
                    )
                    : (
                      <div style={noDataStyle}>
                        <h5 style={{ margin: 0 }}>
                          <Trans>Sleep</Trans>
                        </h5>
                        <p style={{ margin: 0 }}>
                          <Trans>No data available</Trans>
                        </p>
                      </div>
                    )
                  : (
                    <div style={noDataStyle}>
                      <h5 style={{ margin: 0 }}>
                        <Trans>Sleep</Trans>
                      </h5>
                      <p style={{ margin: 0 }}>
                        <Trans>No data available</Trans>
                      </p>
                    </div>
                  )
              }
              {/* eslint-disable-next-line i18next/no-literal-string */}
              {(patientFlags.flags?.patient_app_extended_logging_types?.includes('steps') || stepsLogs.length > 0)
                ? (
                  <div style={{ marginBottom: '10px' }}>
                    <h5>{stepsStr}</h5>
                  </div>
                )
                : (
                  <div style={noDataStyle}>
                    <h5 style={{ margin: 0 }}>
                      <Trans>Steps</Trans>
                    </h5>
                    <p style={{ margin: 0 }}>
                      <Trans>No data available</Trans>
                    </p>
                  </div>
                )}
              <MacroNutrientSummaryCard summaryDate={meal.date} meal={meal.data} />
            </div>
          </Col>
          <Col xs={8}>
            <div className="meal-container w-100 h-100">
              {groupedEntries.map((group) => (
                <div key={group.id}>
                  {recentlyModifiedMeal
                    && group.items.find(item => item.type == 'meal-log' && item.data == recentlyModifiedMeal)
                    && <AutoScrollHere />}

                  <div className="mx-3 my-2 d-flex align-items-center">
                    <div
                      className="meal-card-icon d-inline-flex align-items-center justify-content-center"
                      style={{ backgroundColor: group.def.color }}
                    >
                      <img src={group.def.iconImage} width={20} />
                    </div>
                    <h5 className="d-inline-block m-0 px-2">
                      {group.def.titleTr}
                    </h5>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {group.items.map((item, itemIdx) => {
                      const isVerifyingMeal = item.type == 'meal-log'
                        && item.data.meal_items?.some(mi => mealItemIsVerifying(mi));
                      return (
                        <React.Fragment key={item.id + itemIdx}>
                          {item.type == 'meal-log' && item.data.meal_items
                            ? (
                              <div
                                className="d-inline-block m-1"
                                style={{ cursor: isVerifyingMeal ? 'default' : 'pointer' }}
                                onClick={() => {
                                  if (isVerifyingMeal) {
                                    return;
                                  }
                                  props.onClickItem(item);
                                }}
                              >
                                <MealCard
                                  key={item.id}
                                  meal={item.data}
                                />
                                <MindfulEatingSurveyIcon mealId={item.data.id} />
                                {/* TODO: other types */}
                              </div>
                            )
                            : item.type == 'exercise-log'
                            ? <ExerciseCard exercise={item.data} />
                            : item.type == 'medication-log'
                            ? <MedicationCard medication={item.data} />
                            : item.type == 'emotion-log'
                            ? <EmotionCard emotion={item.data} />
                            : null}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              ))}
              {!groupedEntries.length && (
                <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center">
                  <h4 className="text-muted">
                    <Trans>No meals logged</Trans>
                  </h4>
                  {clinician.flags.clinician_allow_meal_edit && (
                    <span
                      className="addMealBtn"
                      onClick={props.openAddMealPanel}
                      style={{ cursor: 'pointer' }}
                    >
                      <Trans>Add a meal</Trans> <i className="fas fa-plus" />
                    </span>
                  )}
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};
export type FilterOptionsType<T extends keyof ActivityFeedFilterOptions> =
  ActivityFeedFilterOptions[T]['options'][number]['value'];
export type SelectedOptionsType =
  | { logType?: Partial<Record<FilterOptionsType<'logType'>, boolean>> }
  | { mealType?: Partial<Record<FilterOptionsType<'mealType'>, boolean>> }
  | { nutrition?: Partial<Record<FilterOptionsType<'nutrition'>, boolean>> }
  | { glucose?: Partial<Record<FilterOptionsType<'glucose'>, boolean>> };

const FilterPopoverContent = (props: {
  isOpen: boolean,
  filterOptions: SelectedOptionsType,
  onClose: () => void,
  onSearch: (newOptions: SelectedOptionsType) => void,
}) => {
  const { t } = useTranslation();
  const filterPopupRef = useRef<HTMLDivElement>();
  const [localSearchOptions, setLocalSearchOptions] = useState<SelectedOptionsType>(props.filterOptions);

  useEffect(() => {
    function checkIfClickedOutside(e) {
      if (filterPopupRef.current && !filterPopupRef.current.contains(e.target)) {
        props.onClose();
      }
    }
    if (props.isOpen) {
      // eslint-disable-next-line i18next/no-literal-string
      document.addEventListener('click', checkIfClickedOutside);
      return () => {
        // eslint-disable-next-line i18next/no-literal-string
        document.removeEventListener('click', checkIfClickedOutside);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen, props.onClose]);

  useEffect(() => {
    setLocalSearchOptions(props.filterOptions);
  }, [props.filterOptions]);

  const toggleOption = (
    optionCategory: keyof ActivityFeedFilterOptions,
    key: FilterOptionsType<typeof optionCategory>,
  ) => {
    const newOptions = getNewOptions(localSearchOptions, optionCategory, key);
    setLocalSearchOptions(newOptions);
    props.onSearch(newOptions);
  };

  return (
    <div ref={filterPopupRef} className={classnames('search-dropdown', { 'active': props.isOpen })}>
      <div className="search-items">
        {Object.entries(FILTER_OPTIONS(t)).map(([optionCategory, options]) => (
          !((options as any).hidden) && (
            <div key={optionCategory}>
              <p className="mb-1">
                {options.label}
              </p>
              <div className="d-flex flex-wrap mb-3">
                {options.options.map(opt => (
                  <span
                    className={classnames('rx-tag', {
                      'rx-tag-active': localSearchOptions[optionCategory]?.[opt.value],
                    })}
                    key={opt.value}
                    onClick={() => toggleOption(optionCategory as keyof ActivityFeedFilterOptions, opt.value)}
                  >
                    <span>
                      {opt.label}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
};

const PatientLogV2 = (props) => {
  const { patient, clinician } = useStore();
  const { t, i18n } = useTranslation();

  const [filterOptions, setFilterOptions] = useState<SelectedOptionsType>({});
  const [mouseOverFilterBox, setMouseOverFilterBox] = useState(false);
  const [selectedMeal, setSelectedMeal] = useState<
    {
      meal: MealResponse,
      activityFeedSectionIdx: number,
    } | null
  >(null);
  const [filterPopover, setFilterPopover] = useState<boolean>(false);
  const timelineContainerRef = useRef<HTMLDivElement>();
  const [showAddMeal, setShowAddMeal] = useState(false);
  const { dateRangeFilter, setDateRangeFilter, hasUpdatedDates } = useDateRange();

  // Clean up cache when component unmounts or patient changes
  useEffect(() => {
    // Clear cache when patient changes
    useMealCache.setState({});

    return () => {
      // Clear cache when component unmounts
      useMealCache.setState({});
    };
  }, [patient.patient_id]);

  const filterOptionsLabels = FILTER_OPTIONS(t);

  const showSearchFilters = useMemo(() => {
    if (!filterOptions) {
      return false;
    }
    return Object.values(filterOptions).some((category) => Object.values(category).some((value) => value));
  }, [filterOptions]);

  const [dates, setDates] = useState({
    oldestDate: DateTime.local().minus({ days: 65 }),
    newestDate: null,
  });
  const { calendarActivityIndicators, isFetching } = useCalendarActivityIndicators(dates);
  const [start, end] = dateRangeFilter.rangePickerValue.map(d => d.format('YYYY-MM-DD'));

  const mealTimeline = usePatientActivityFeed({
    filterOptions,
    filter: {
      timestamp: {
        gte: start,
        lt: moment(end).add(1, 'day').format('YYYY-MM-DD'),
      },
    },
    patientId: patient.patient_id,
    enabled: hasUpdatedDates,
  });

  const mealTimelineQuery = mealTimeline.query;

  const executeSearch = (newOptions: SelectedOptionsType) => {
    Object.keys(newOptions).forEach(filterType => {
      track(ANALYTICS_EVENTS.FOOD_LOG_FILTER_USED, {
        filter: filterType,
      });
    });
    setFilterOptions(newOptions);
  };

  const removeFilter = (optionCategory, filter) => {
    const newOptions = getNewOptions(filterOptions, optionCategory, filter);
    setFilterOptions(newOptions);
  };

  const activityFeedDays = useMemo(() => {
    if (showSearchFilters) {
      return mealTimeline.filteredFeedEntries;
    }
    return mealTimeline.feedEntries;
  }, [showSearchFilters, mealTimeline.feedEntries, mealTimeline.filteredFeedEntries]);

  const intersectionObserver = useRef<IntersectionObserver>();

  const lastPatientActivityRef = useCallback((entries: IntersectionObserverEntry[] | HTMLDivElement) => {
    if (intersectionObserver.current) {
      intersectionObserver.current.disconnect();
    }

    intersectionObserver.current = new IntersectionObserver((entries) => {
      if (mealTimelineQuery.isFetchingNextPage) {
        return;
      }
      if (entries[0].isIntersecting && mealTimelineQuery.hasNextPage) {
        mealTimelineQuery.fetchNextPage();
      }
    });
    if (entries) {
      intersectionObserver.current.observe(entries as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mealTimelineQuery.isFetchingNextPage, mealTimelineQuery.hasNextPage]);

  const handleMealCardClick = (meal: MealResponse, idx: number) => {
    track(ANALYTICS_EVENTS.MEAL_CARD_CLICKED, {
      'Meal ID': meal.id,
      'Patient ID': patient.patient_id,
      'Source': 'Food Log',
    });
    setSelectedMeal({
      meal,
      activityFeedSectionIdx: idx,
    });
  };

  return (
    <>
      <Row className="d-flex align-items-center toolbar">
        <Col xs="auto" className="d-flex ml-auto tools-wrapper align-items-center">
          {clinician.flags.clinician_allow_meal_edit && (
            <span onClick={() => setShowAddMeal(true)}>
              <i className="fas fa-plus rounded-btn" title="Add Meal" />
            </span>
          )}
          {showSearchFilters
            && (
              <div
                className={`selected-filters ${mouseOverFilterBox ? 'bigger-box' : 'selected-filters'}`}
                onMouseOver={() => setMouseOverFilterBox(true)}
                onMouseLeave={() => setMouseOverFilterBox(false)}
              >
                {Object.entries(filterOptions).map(([filterKey, filterValues]) => (
                  Object.entries(filterValues).map(([value, isSelected]) => {
                    const optionDef = filterOptionsLabels[filterKey];
                    const valueDef = optionDef?.options?.find(o => o.value === value);

                    return !!isSelected && valueDef && (
                      <span className="rx-tag" key={`${filterKey}:${value}`}>
                        {valueDef.cardLabel || valueDef.label}
                        <span
                          className="p-1 text-danger"
                          onClick={() => removeFilter(filterKey, value)}
                        >
                          <i className="fa fa-times-circle" title="Remove Filter" />
                        </span>
                      </span>
                    );
                  })
                ))}
              </div>
            )}

          <div className="position-relative">
            <span onClick={() => setFilterPopover(!filterPopover)}>
              <i className="fas fa-filter rounded-btn" title="Filter Meals" />
            </span>
            <FilterPopoverContent
              isOpen={filterPopover}
              onClose={() => setFilterPopover(false)}
              filterOptions={filterOptions || {}}
              onSearch={executeSearch}
            />
          </div>
          <span
            style={{
              textTransform: 'uppercase',
              fontSize: '14px',
              letterSpacing: '0.2rem',
            }}
          >
            <Trans>filter by date</Trans>
          </span>
          <ConfigProvider
            locale={i18n.language === 'fr' ? frCA : enUS}
            theme={{
              components: {
                DatePicker: {
                  cellHeight: 32,
                  cellWidth: 32,
                },
              },
            }}
          >
            <RangePickerWithActivityIndicators
              calendarActivityIndicators={calendarActivityIndicators}
              isFetching={isFetching}
              filter={dateRangeFilter}
              dates={dates}
              setFilter={setDateRangeFilter}
              setDates={setDates}
            />
            <DateRangeDropdown setDateRangeFilter={setDateRangeFilter} />
          </ConfigProvider>
        </Col>
      </Row>
      <div className="timeline-container" ref={timelineContainerRef}>
        {activityFeedDays.map((section, idx) => {
          const dayView = (
            <PatientActivityLogDayView
              key={idx}
              section={section}
              openAddMealPanel={() => setShowAddMeal(true)}
              onClickItem={(item) => {
                if (item.type != 'meal-log') {
                  // for now, only meals are clickable
                  return;
                }
                handleMealCardClick(item.data, idx);
              }}
            />
          );
          if (activityFeedDays.length === idx + 1) {
            return (
              <div ref={lastPatientActivityRef} key={section.id}>
                {dayView}
                <div className="text-center scroll-loader" style={{ height: '125px' }}>
                  {mealTimelineQuery.isFetchingNextPage || !hasUpdatedDates
                    ? <LoadingSpinner />
                    : !mealTimelineQuery.hasNextPage
                    ? (
                      <h5>
                        <Trans>
                          All food records entered by the patient during the selected date range are shown above. To
                          view records from a different period, please adjust the date filter.
                        </Trans>
                      </h5>
                    )
                    : null}
                </div>
              </div>
            );
          }
          return dayView;
        })}
        {!activityFeedDays.length && (
          <div className="w-100 p-4 d-flex flex-column align-items-center justify-content-center">
            {mealTimelineQuery.isLoading || !hasUpdatedDates
              ? <LoadingSpinner />
              : mealTimelineQuery.isError
              ? (
                <>
                  <h4 className="text-muted">
                    <Trans>Unexpected error loading meals</Trans>
                  </h4>
                  <span>
                    {'' + mealTimelineQuery.error}
                  </span>
                </>
              )
              : (
                <>
                  <h4 className="text-muted">
                    <Trans>No meals logged</Trans>
                  </h4>
                  {clinician.flags.clinician_allow_meal_edit && (
                    <span
                      className="addMealBtn"
                      onClick={() => setShowAddMeal(true)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Trans>Add a meal</Trans> <i className="fas fa-plus" />
                    </span>
                  )}
                </>
              )}
          </div>
        )}
      </div>
      {!!selectedMeal && (
        <ShowMealV2
          selectedMeal={selectedMeal.meal}
          activityData={activityFeedDays[selectedMeal.activityFeedSectionIdx]?.data || []}
          onHide={() => setSelectedMeal(null)}
          initialMode="view"
        />
      )}
      {showAddMeal && (
        <ShowMealV2
          selectedMeal={null}
          onHide={() => setShowAddMeal(false)}
          activityData={[]}
          initialMode="add"
        />
      )}
    </>
  );
};

export default PatientLogV2;
